/**
 * RhNotesFraisAValider — bloc « Notes de frais à valider » (écran 3 du mockup),
 * dans l'onglet RH « À valider ». 1er niveau (manager). Reproduit le mockup.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { RhWho } from './rh-ui';
import type { NdfNote, NdfLigne } from '@roronoa-collectivites/shared-types';

const eur = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;

export function RhNotesFraisAValider() {
  const [items, setItems] = useState<Array<NdfNote & { nbLignes: number; alerte: boolean }>>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [detail, setDetail] = useState<NdfNote | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [motif, setMotif] = useState('');

  async function charger() {
    try { setItems(await api.notesFrais.notes.aValiderN1()); } catch { /* ignore */ }
    finally { setLoaded(true); }
  }
  useEffect(() => { void charger(); }, []);

  async function valider(id: string) {
    setError(null); setMsg(null);
    try { await api.notesFrais.notes.validerN1(id); setMsg('Note validée — envoyée à la comptabilité.'); setDetail(null); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function confirmerRefus() {
    if (!refus || !motif.trim()) return;
    setError(null);
    try { await api.notesFrais.notes.refuser(refus, motif.trim()); setMsg('Note refusée.'); setRefus(null); setMotif(''); setDetail(null); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function ouvrirDetail(id: string) {
    try { setDetail(await api.notesFrais.notes.get(id)); } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  if (!loaded) return null;
  if (items.length === 0) return msg ? <Alert variant="success">{msg}</Alert> : null;

  return (
    <>
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}
      <Card className="overflow-hidden p-0">
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <Badge variant="urgent">{items.length}</Badge>
          <h2 className="font-display text-sm font-semibold text-ink">Notes de frais à valider</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Déclarant</th>
                <th className="px-4 py-2.5 font-medium">Note</th>
                <th className="px-4 py-2.5 font-medium">Dépenses</th>
                <th className="px-4 py-2.5 text-right font-medium">Montant</th>
                <th className="px-4 py-2.5 font-medium">Alerte</th>
                <th className="px-4 py-2.5 text-right font-medium">Décision</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5"><RhWho nom={n.declarantNom ?? '—'} /></td>
                  <td className="px-4 py-2.5 text-ink">{n.titre}</td>
                  <td className="px-4 py-2.5 text-ink-subtle">{n.nbLignes} ligne{n.nbLignes > 1 ? 's' : ''}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink">{eur(n.montantTotal)}</td>
                  <td className="px-4 py-2.5">{n.alerte ? <Badge variant="urgent">Plafond</Badge> : <Badge variant="success">RAS</Badge>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="ghost" size="sm" onClick={() => void ouvrirDetail(n.id)}>Détail</Button>{' '}
                    <Button variant="secondary" size="sm" onClick={() => void valider(n.id)}>Valider</Button>{' '}
                    <button type="button" onClick={() => { setRefus(n.id); setMotif(''); }} className="text-xs text-ink-subtle hover:text-danger">Refuser</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Détail */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={detail ? `Détail — ${detail.titre}` : ''}
        footer={detail && <><Button variant="danger" onClick={() => { setRefus(detail.id); setMotif(''); }}>Refuser (motif)…</Button><Button variant="secondary" onClick={() => void valider(detail.id)}>Valider → envoyer à la comptabilité</Button></>}>
        {detail && <DetailLignes note={detail} />}
      </Dialog>

      {/* Refus */}
      <Dialog open={!!refus} onClose={() => setRefus(null)} title="Refuser la note de frais"
        footer={<><Button variant="ghost" onClick={() => setRefus(null)}>Annuler</Button><Button variant="danger" disabled={!motif.trim()} onClick={() => void confirmerRefus()}>Confirmer le refus</Button></>}>
        <div><div className="mb-1 text-xs text-ink-subtle">Motif (communiqué au déclarant)</div>
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} className="w-full rounded-r-md border border-border bg-surface/60 px-2.5 py-2 text-sm text-ink" /></div>
      </Dialog>
    </>
  );
}

function DetailLignes({ note }: { note: NdfNote }) {
  async function voir(l: NdfLigne) {
    // la clé n'est pas exposée ici ; on ouvre via l'API justificatif si dispo côté note
    void l;
  }
  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-ink-subtle">
            <th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Libellé</th>
            <th className="py-2 pr-3 font-medium">Détail</th><th className="py-2 pr-3 text-right font-medium">Montant</th><th className="py-2 font-medium">Justif.</th>
          </tr>
        </thead>
        <tbody>
          {(note.lignes ?? []).map((l) => (
            <tr key={l.id} className="border-b border-border/60 last:border-0">
              <td className="py-2 pr-3"><Badge variant={l.distanceKm != null ? 'rh' : 'neutral'}>{l.typeLibelle ?? '—'}</Badge></td>
              <td className="py-2 pr-3 text-ink">{l.libelle ?? '—'}</td>
              <td className="py-2 pr-3 text-ink-subtle">{l.distanceKm != null ? `${l.distanceKm} km · ${l.puissanceFiscale} CV` : l.depassePlafond ? 'plafond dépassé' : (l.montantTva != null ? `TVA ${eur(l.montantTva)}` : '—')}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-ink">{eur(l.montantTtc)}</td>
              <td className="py-2">{l.distanceKm != null ? <span className="text-ink-subtle text-xs">barème</span> : l.aJustificatif ? <button type="button" onClick={() => void voir(l)} className="text-xs text-rh underline">📎 Voir</button> : <span className="text-ink-subtle text-xs">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <span className="text-lg font-bold text-ink">{eur(note.montantTotal)}</span>
      </div>
    </div>
  );
}
